/**
 * CustomersList — AR workbench landing page at /invoices/customers.
 *
 *   Top: filter bar (search, has-overdue, auto-remind, sort) + bulk Send-statement button.
 *   Body: sortable table of every customer with at least one issued invoice.
 *   Each row links to /invoices/customer/:email (the relationship view).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import {
  ArrowsCounterClockwise, MagnifyingGlass, EnvelopeSimple, BellSlash,
  Bell, Users, ArrowSquareOut, CheckSquare, Square, ListChecks,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  getCustomersList, sendBulkStatements,
} from '../lib/api';
import ScheduleFollowupDialog from '../components/ScheduleFollowupDialog';

function formatCurrency(amount, ccy) {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: ccy || 'AUD' })
      .format(amount || 0);
  } catch (_) { return `${(amount || 0).toFixed(2)} ${ccy || 'AUD'}`; }
}
function pct(v) { return v == null ? '—' : `${Math.round(v * 100)}%`; }
function relTime(iso) {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return `${Math.floor(d / 30)}mo ago`;
}

const SORT_LABEL = {
  outstanding_desc: 'Outstanding $ ↓',
  paid_90d_desc: 'Paid 90d ↓',
  last_reminded_asc: 'Stalest reminders',
  name_asc: 'Name A→Z',
};

export default function CustomersList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [hasOverdue, setHasOverdue] = useState(false);
  const [autoRemind, setAutoRemind] = useState('');  // '', 'on', 'off'
  const [sort, setSort] = useState('outstanding_desc');
  const [selected, setSelected] = useState(new Set());
  const [sending, setSending] = useState(false);
  const [followupTarget, setFollowupTarget] = useState(null); // {email, name}

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await getCustomersList({
        search: search.trim() || undefined,
        has_overdue: hasOverdue,
        auto_remind: autoRemind || undefined,
        sort,
      });
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error('Could not load customers');
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, [hasOverdue, autoRemind, sort]); // eslint-disable-line react-hooks/exhaustive-deps

  // search debounce
  useEffect(() => {
    const t = setTimeout(refresh, 350);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const overdueEmails = useMemo(
    () => items.filter((x) => x.has_overdue && x.outstanding_total > 0).map((x) => x.email),
    [items],
  );

  const toggleRow = (email) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(email)) n.delete(email); else n.add(email);
      return n;
    });
  };
  const toggleAllOverdue = () => {
    if (overdueEmails.every((e) => selected.has(e))) {
      setSelected(new Set());
    } else {
      setSelected(new Set(overdueEmails));
    }
  };

  const handleBulkSend = async () => {
    if (selected.size === 0) return;
    setSending(true);
    try {
      const r = await sendBulkStatements(Array.from(selected));
      const sent = r.data?.sent?.length || 0;
      const skipped = r.data?.skipped?.length || 0;
      const errors = r.data?.errors?.length || 0;
      toast.success(`Sent ${sent} statement${sent === 1 ? '' : 's'} · skipped ${skipped} · errors ${errors}`);
      setSelected(new Set());
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk send failed');
    } finally { setSending(false); }
  };

  const allOverdueSelected = overdueEmails.length > 0 && overdueEmails.every((e) => selected.has(e));

  return (
    <Layout>
      <div className="p-6 space-y-4" data-testid="customers-list-page">
        <div className="flex items-center gap-2">
          <Users size={18} weight="bold" className="text-violet-600" />
          <h1 className="text-2xl font-bold tracking-tight">Customers</h1>
          <span className="text-xs text-zinc-500">· AR workbench</span>
        </div>

        {/* Filter bar */}
        <div className="bg-white border border-zinc-200 p-3 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[12rem] max-w-[24rem]">
            <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name or email"
              className="pl-7 h-8 text-xs"
              data-testid="customers-search"
            />
          </div>
          <label className="inline-flex items-center gap-1.5 px-2 py-1 border border-zinc-200 cursor-pointer hover:bg-zinc-50">
            <input
              type="checkbox"
              checked={hasOverdue}
              onChange={(e) => setHasOverdue(e.target.checked)}
              data-testid="customers-filter-overdue"
            />
            <span className="text-xs">Has overdue</span>
          </label>
          <select
            value={autoRemind}
            onChange={(e) => setAutoRemind(e.target.value)}
            className="px-2 h-8 text-xs border border-zinc-200 bg-white"
            data-testid="customers-filter-autoremind"
          >
            <option value="">Auto-remind: any</option>
            <option value="on">Auto-remind: ON</option>
            <option value="off">Auto-remind: OFF</option>
          </select>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="px-2 h-8 text-xs border border-zinc-200 bg-white"
            data-testid="customers-sort"
          >
            {Object.entries(SORT_LABEL).map(([k, v]) => (<option key={k} value={k}>{v}</option>))}
          </select>
          <Button onClick={refresh} variant="outline" size="sm" data-testid="customers-refresh">
            <ArrowsCounterClockwise size={12} className="mr-1" /> Refresh
          </Button>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-[11px] text-zinc-500">{items.length} customer{items.length === 1 ? '' : 's'} · {selected.size} selected</span>
            <Button
              onClick={handleBulkSend}
              disabled={selected.size === 0 || sending}
              className="bg-violet-600 hover:bg-violet-700"
              size="sm"
              data-testid="customers-bulk-send-statement"
            >
              <EnvelopeSimple size={12} weight="bold" className="mr-1" />
              {sending ? 'Sending…' : `Send statement (${selected.size})`}
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-zinc-200 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b bg-zinc-50">
              <tr>
                <th className="py-2 px-3 text-left w-8">
                  <button
                    type="button"
                    onClick={toggleAllOverdue}
                    title="Select all overdue customers"
                    className="text-zinc-500 hover:text-violet-700"
                    data-testid="customers-select-all-overdue"
                  >
                    {allOverdueSelected
                      ? <CheckSquare size={14} weight="fill" />
                      : <Square size={14} />}
                  </button>
                </th>
                <th className="py-2 px-3 text-left">Customer</th>
                <th className="py-2 px-3 text-right">Invoices</th>
                <th className="py-2 px-3 text-right">Outstanding</th>
                <th className="py-2 px-3 text-right">Paid · 90d</th>
                <th className="py-2 px-3 text-left">Auto-remind</th>
                <th className="py-2 px-3 text-left">Last reminded</th>
                <th className="py-2 px-3 text-right">Pay-rate</th>
                <th className="py-2 px-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 ? (
                <tr><td colSpan={9} className="py-8 text-center text-zinc-400 italic">Loading customers…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={9} className="py-8 text-center text-zinc-400 italic" data-testid="customers-empty">No customers match the current filters.</td></tr>
              ) : items.map((c) => {
                const isSel = selected.has(c.email);
                return (
                  <tr key={c.email} className={`border-b border-zinc-100 hover:bg-zinc-50 ${isSel ? 'bg-violet-50/40' : ''}`} data-testid={`customers-row-${c.email}`}>
                    <td className="py-2 px-3">
                      <button
                        type="button"
                        onClick={() => toggleRow(c.email)}
                        disabled={!c.has_overdue || c.outstanding_total <= 0}
                        title={c.has_overdue ? 'Include in bulk send' : 'No overdue invoices'}
                        className="disabled:opacity-30"
                        data-testid={`customers-row-select-${c.email}`}
                      >
                        {isSel ? <CheckSquare size={14} weight="fill" className="text-violet-600" /> : <Square size={14} className="text-zinc-400" />}
                      </button>
                    </td>
                    <td className="py-2 px-3">
                      <Link
                        to={`/invoices/customer/${encodeURIComponent(c.email)}`}
                        className="block hover:text-violet-700"
                        data-testid={`customers-row-link-${c.email}`}
                      >
                        <div className="font-bold truncate max-w-[18rem]">{c.name}</div>
                        <div className="text-[10px] font-mono text-zinc-500 truncate max-w-[18rem]">{c.email}</div>
                      </Link>
                    </td>
                    <td className="py-2 px-3 text-right font-mono">
                      {c.invoices_count}
                      {c.outstanding_count > 0 && (
                        <span className="ml-1 text-[10px] text-zinc-500">({c.outstanding_count} open)</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-right font-mono font-bold">
                      {formatCurrency(c.outstanding_total, c.currency)}
                      {c.has_overdue && (
                        <span className="ml-1 px-1 py-0.5 text-[9px] font-bold border bg-rose-50 border-rose-300 text-rose-700 uppercase">overdue</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-right font-mono text-emerald-700">{formatCurrency(c.paid_90d, c.currency)}</td>
                    <td className="py-2 px-3">
                      {c.do_not_auto_remind ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold border bg-zinc-100 border-zinc-400 text-zinc-700">
                          <BellSlash size={10} weight="bold" /> OFF
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold border bg-emerald-50 border-emerald-300 text-emerald-700">
                          <Bell size={10} /> ON
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-zinc-500">
                      {c.last_reminder_at ? (
                        <span title={new Date(c.last_reminder_at).toLocaleString()}>{relTime(c.last_reminder_at)} · {c.reminders_total}×</span>
                      ) : (
                        <span className="italic text-zinc-400">never</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-right">
                      {c.pay_rate_within_7d == null ? (
                        <span className="text-zinc-400">—</span>
                      ) : (
                        <span className={`px-1.5 py-0.5 text-[10px] font-bold border ${
                          c.pay_rate_within_7d >= 0.5 ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                            : c.pay_rate_within_7d > 0 ? 'bg-amber-50 border-amber-300 text-amber-700'
                            : 'bg-zinc-50 border-zinc-300 text-zinc-500'
                        }`}>{pct(c.pay_rate_within_7d)}</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => setFollowupTarget({ email: c.email, name: c.name })}
                          className="inline-flex items-center gap-1 px-2 py-1 border border-violet-300 bg-violet-50 text-violet-700 text-[10px] font-bold uppercase hover:bg-violet-100"
                          data-testid={`customers-row-followup-${c.email}`}
                          title={`Schedule follow-up for ${c.name}`}
                        >
                          <ListChecks size={11} weight="bold" />
                        </button>
                        <Link
                          to={`/invoices/customer/${encodeURIComponent(c.email)}`}
                          className="inline-flex items-center gap-1 px-2 py-1 border border-zinc-300 text-[10px] font-bold uppercase hover:bg-zinc-50"
                          data-testid={`customers-row-open-${c.email}`}
                        >
                          Open <ArrowSquareOut size={10} weight="bold" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <ScheduleFollowupDialog
        open={!!followupTarget}
        onOpenChange={(o) => !o && setFollowupTarget(null)}
        customerEmail={followupTarget?.email}
        customerName={followupTarget?.name}
      />
    </Layout>
  );
}
